import { Link } from "@mui/material";

import { faqs, howItWorks, AnalyticsHeader } from "./helpers";
import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import {
  DefaultRegistrationTerms,
  DefaultCheckoutTerms,
} from "../../default_terms_and_conditions";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

import StaticSeatMapImage from "public/images/hawks/seat-map.jpg";

const Background =
  "https://ss-leagues.s3.amazonaws.com/NBA/hawks-background.jpg";
const Logo = "https://ss-leagues.s3.amazonaws.com/NBA/atlanta-hawks.png";
const Banner = "https://ss-leagues.s3.amazonaws.com/NBA/hawks-banner.jpg";

const config: TeamConfig = {
  teamSchema: "atlantahawks_flex",
  browserTitle: "Atlanta Hawks Flex Plans",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Atlanta Hawks",

  buckets: {
    enabled: true,
    list: [
      {
        id: 1,
        min: 2,
        max: 15,
        description: "Pick 2 games and get tickets to one free game.",
        isPrimary: true,
        name: "Pick 2 Games",
        remainingBucketLanguage: "",
        scrollToTopWhenSelecting: true,
        dontCloseWhenHitMin: true,
        disableAccordionExpand: true,
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-00062-hawks-sec",
    mapId: "blockmap2d",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.purchaseSeats,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 15,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: false,
    checkoutTimerEnabled: false,
    isPerksEnabled: true,

    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    beaconId: "",
    attrkPixelId: "",
    googleAnalyticsId: "G-MG3ZLEC1VE",
    trackGoogleAnalyticsOnCheckout: true,
    AnalyticsHeader,
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  shouldCheckZipCode: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/hawks/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    logoNameImage: "",
    faviconImage: Logo,
    findGamesBannerImage: Banner,
    authBackgroundImage: Background,
    checkoutStatusPageImage: Background,
    logoImage: `${Logo}?width=64&height=64`,
    seatMap3dPreviewImage: StaticSeatMapImage.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    customAdaLabel: (
      <>
        For accessible seating, please call the ticket office at{" "}
        <Link
          href="tel:4048783000"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (404) 878-3000
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#C8102E",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText:
        "to continue — once you've made your selections, you'll be able to choose your free game.",
      progressBarSuccessText:
        "You're good to go! Add more events or select Continue to view seating options - once you've picked your seats, you'll be able to choose your free game.",
      chooseAddOnsTabTitle: "Choose Free Game",
      progressBarAddLabel: "Select",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: "Thank you for your Atlanta Hawks purchase!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
